import DoubleBookingScreen from "@components/Booking/DoubleBooking";
import {
  CurrentPatientQuery,
  useCurrentPatientQuery,
} from "@components/Booking/graphql/__generated__/current-patient.type";
import PatientSelection from "@components/Booking/PatientSelection";
import {BookingPracticeAppointmentReasonFieldsFragment} from "@page-containers/booking/graphql/__generated__/FetchAppointmentReasons.type";
import {useParsedBookingQuery} from "@page-containers/booking/utils/bookingQuery";
import {BookingStep} from "@page-containers/booking/utils/bookingStep";
import React, {ReactElement} from "react";

import {MonolithLocationFields} from "../../../_services/graphql/queries/location";
import AppointmentFor from "../../../components/v5/Booking/AppointmentFor";
import CompleteCheckIn from "../../../components/v5/Booking/Complete/CompleteCheckIn";
import EnterEmailPage from "../../../components/v5/Booking/EnterEmail/EnterEmailPage";
import SlotPicker from "../../../components/v5/Booking/SlotPicking/SlotPicker";
import SlotUnavailablePage from "../../../components/v5/Booking/SlotPicking/SlotUnavailablePage";
import Icon from "../../../components/v5/Icon";
import {useSessionStorageItem} from "../../../utils/browser-storage/useBrowserStorage";
import {SessionStorageKey} from "../../../utils/sessionStorage";
import {getStartOfDay} from "../../../utils/timeUtils";
import AppointmentReasonPage from "@components/Booking/AppointmentReason";
import {LocationSelectionPage} from "@components/Booking/LocationSelection";
import {useContinueAbandonedFlow} from "./bookingProgress/useContinueAbandonedFlow";
import {guess} from "@services/api";
import {useLoadApptReasons} from "@hooks/useLoadApptReasons";

const getCurrentPatient = (data?: CurrentPatientQuery) =>
  data?.me?.__typename === "PatientUser" ? data.me.defaultPatient : null;

export default function useBookingStep(
  openSexAtBirthBottomSheet: () => void,
  location: MonolithLocationFields | null,
  practiceAppointmentReasonLoading: boolean,
  locationDataLoading: boolean,
  practiceAppointmentReason?: BookingPracticeAppointmentReasonFieldsFragment | null,
): [BookingStep, ReactElement] {
  const {
    doubleBookingOf,
    selectedTime,
    selectedSlotIds,
    isSelectedTimeUnavailable,
    appointmentId,
    rescheduling,
    initialCalendarTime,
    specialtyId,
    selectedPatientId,
    selectedTopic,
    confirmationEmailSent,
  } = useParsedBookingQuery();

  const abandonedFlow = useContinueAbandonedFlow();
  const dropoutVisitorId = abandonedFlow.dropoutVisitorId || undefined;
  const reasons = useLoadApptReasons(true);

  const patientQuery = useCurrentPatientQuery();
  const patient = getCurrentPatient(patientQuery.data);
  const email = useSessionStorageItem(SessionStorageKey.WebBookingEmail);
  const apptReasonId = practiceAppointmentReason?.appointmentReason?.id;
  const apptReasonSlug = practiceAppointmentReason?.slug;

  const loadingReturn: [BookingStep, ReactElement] = [
    BookingStep.LOADING,
    <div key="loading" className="w-full h-full flex items-center justify-center ">
      <Icon icon="logo" className="text-3xl leading-none text-gray-800 animate-pulse" />
    </div>,
  ];

  // These params are needed on all screens... so w/o them, the URL isn't even well-formed.
  if (
    practiceAppointmentReasonLoading ||
    locationDataLoading ||
    patientQuery.loading ||
    abandonedFlow.loading
  ) {
    return loadingReturn;
  }

  // First we ask users to pick an appointment reason
  if (!practiceAppointmentReason || !apptReasonId || !apptReasonSlug || !specialtyId) {
    return [
      BookingStep.APPT_REASON_SELECTION,
      <AppointmentReasonPage
        key={1}
        location={location ?? undefined}
        reasons={reasons}
        selectedTopic={selectedTopic}
      />,
    ];
  }

  // Then a location
  if (!location) {
    return [
      BookingStep.IN_BOOKING_LOCATION_SELECTION,
      <LocationSelectionPage
        key={1}
        practiceAppointmentReason={practiceAppointmentReason}
        specialtyId={specialtyId}
      />,
    ];
  }

  // Then an appointment time.
  if (!selectedTime) {
    if (rescheduling && selectedPatientId) {
      return [
        BookingStep.RESCHEDULING,
        <SlotUnavailablePage
          location={location}
          practiceAppointmentReason={practiceAppointmentReason}
          specialtyId={specialtyId}
          patientId={selectedPatientId}
          dropoutVisitorId={dropoutVisitorId}
          key={1}
        />,
      ];
    } else {
      return [
        BookingStep.TIME_SELECTION,
        <SlotPicker
          location={location}
          practiceAppointmentReason={practiceAppointmentReason}
          specialtyId={specialtyId}
          calendarTime={getStartOfDay(
            initialCalendarTime || Date.now(),
            location.timezone ?? guess(),
          )}
          dropoutVisitorId={dropoutVisitorId}
          key={1}
        />,
      ];
    }
  }

  // Once the time chosen, we need to get the user logged in. Since email is PHI, we can't
  // put it in the URL... so these next two screens are handled as a batch.
  //
  // "confirmationEmailSent" exists to prevent a nasty double booking bug. If the patient enters
  // an email we recognize, the EnterEmailPage sends an email w/ one-time login link
  // to finish booking. That screen opens in a new tab, and does a full login. Once logged in,
  // Chrome (and probably other browsers) will run the javascript in _this_ tab, causing
  // the patients to double-book an appointment. "confirmationEmailSent" helps make sure
  // that we continue rendering the EnterEmailPage and _don't_ book in _this_ tab.
  //
  // EDIT: I think this issue is fixed now, but I'm not 100% sure so leaving for now.
  if (confirmationEmailSent || !patient) {
    if (email) {
      return [
        BookingStep.ACCOUNT_CREATION,
        <AppointmentFor
          location={location}
          time={selectedTime}
          apptReasonId={apptReasonId}
          openSexAtBirthBottomSheet={openSexAtBirthBottomSheet}
          specialtyId={specialtyId}
          prefilledData={abandonedFlow.schedulingFlowState?.patientInfo}
          dropoutVisitorId={dropoutVisitorId}
          key={1}
        />,
      ];
    }

    return [
      BookingStep.ACCOUNT_SELECTION,
      <EnterEmailPage
        location={location}
        reasonId={apptReasonId}
        reasonSlug={apptReasonSlug}
        time={selectedTime}
        specialtyId={specialtyId}
        dropoutVisitorId={dropoutVisitorId}
        key={1}
      />,
    ];
  }
  // return loadingReturn;

  // Once authed, family account users need to select which patient is booking.
  if (!selectedPatientId && selectedSlotIds) {
    return [
      BookingStep.PATIENT_SELECTION,
      <PatientSelection
        location={location}
        selectedTime={selectedTime}
        apptReasonId={apptReasonId}
        locationId={location.id}
        specialtyId={specialtyId}
        slotIds={selectedSlotIds}
        dropoutVisitorId={dropoutVisitorId}
        key={1}
      />,
    ];
  }

  // If two people book at the same time, or the cache was stale so the slot is gone,
  // someone will have to choose another slot. This is rare... but definitely happens.
  if (isSelectedTimeUnavailable && selectedPatientId) {
    return [
      BookingStep.TIME_RESELECTION,
      <SlotUnavailablePage
        location={location}
        practiceAppointmentReason={practiceAppointmentReason}
        specialtyId={specialtyId}
        patientId={selectedPatientId}
        dropoutVisitorId={dropoutVisitorId}
        key={1}
      />,
    ];
  }

  // If the user already has a similar appointment booked around the same time,
  // force them to choose whether to book or reschedule.
  if (doubleBookingOf && !appointmentId && selectedSlotIds && selectedPatientId) {
    return [
      BookingStep.DOUBLE_BOOKING_GUARD,
      <DoubleBookingScreen
        apptReasonId={apptReasonId}
        selectedPatientId={selectedPatientId}
        selectedSlotIds={selectedSlotIds}
        selectedTime={selectedTime}
        specialtyId={specialtyId}
        location={location}
        dropoutVisitorId={dropoutVisitorId}
        key={1}
      />,
    ];
  }

  // If we don't have an appointment ID yet, there should be a booking call in progress
  // from useAttemptBooking()
  if (!appointmentId) {
    return loadingReturn;
  }

  const finalStep = rescheduling
    ? BookingStep.RESCHEDULING_CONFIRMATION
    : doubleBookingOf
    ? BookingStep.ORIGINAL_APPOINTMENT_CONFIRMATION
    : BookingStep.APPOINTMENT_CONFIRMATION;

  return [
    finalStep,
    <CompleteCheckIn
      location={location}
      selectedTime={selectedTime}
      practiceAppointmentReason={practiceAppointmentReason}
      appointmentId={appointmentId}
      specialtyId={specialtyId}
      key={1}
    />,
  ];
}
